import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { Permission } from '../types/permissions';

/**
 * Check if the current user has a specific permission.
 */
export function usePermission(permission: Permission): boolean {
  const { adminUser } = useAuth();

  return useMemo(() => {
    if (!adminUser) return false;
    const perms = adminUser.permissions ?? [];
    // system:full_access grants all permissions
    if (perms.includes('system:full_access')) return true;
    return perms.includes(permission);
  }, [adminUser, permission]);
}

/**
 * Check if the current user has a specific permission at a specific property.
 */
export function usePropertyPermission(
  permission: Permission,
  propertyId: string | undefined,
): boolean {
  const { adminUser } = useAuth();

  return useMemo(() => {
    if (!adminUser || !propertyId) return false;
    const perms = adminUser.permissions ?? [];
    if (perms.includes('system:full_access')) return true;
    if (!perms.includes(permission)) return false;
    // Check if user has access to this property
    return adminUser.accessiblePropertyIds.includes(propertyId);
  }, [adminUser, permission, propertyId]);
}

/**
 * Check if the current user has a specific role.
 */
export function useHasRole(role: string): boolean {
  const { adminUser } = useAuth();

  return useMemo(() => {
    if (!adminUser) return false;
    if (adminUser.primaryRole === role) return true;
    return (adminUser.roleIds ?? []).includes(role);
  }, [adminUser, role]);
}

/**
 * Check if user can access a specific property.
 */
export function useCanAccessProperty(propertyId: string | undefined): boolean {
  const { adminUser } = useAuth();

  return useMemo(() => {
    if (!adminUser || !propertyId) return false;
    if (adminUser.isSuperAdmin) return true;
    return adminUser.accessiblePropertyIds.includes(propertyId);
  }, [adminUser, propertyId]);
}

/**
 * Returns auth loading and authentication status.
 */
export function useRequireAuth(): {
  isLoading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
} {
  const { user, adminUser, loading } = useAuth();

  return useMemo(
    () => ({
      isLoading: loading,
      isAuthenticated: !!user && !!adminUser,
      isSuperAdmin: adminUser?.isSuperAdmin ?? false,
    }),
    [user, adminUser, loading],
  );
}
